// Compose: Brief, Harmonie, Topline, Groove, Ideen-Pool, Text-Werkstatt, Urheberschaft.
// Alles bearbeitet das echte Projekt. Engine-Vorschläge sind als KI markiert und zählen nicht in die Splits.
import { useMemo, useRef, useState } from 'react';
import { detectNotes, quantizeNotes, parseMidi, writeMidi, voicing, parseChord, parseKey, snapToKey, type Note, type Project, type Clip } from '@srs/shared';
import { barAtSec, beatAt, meterAt, secAt, secOfBar } from '@srs/shared';
import { useStudio } from '../state/store.ts';
import { engine } from '../audio/engine.ts';
import { voiceFor } from '../audio/instruments.ts';
import { emptyTrack, uid } from '../state/demo.ts';
import { Panel, toast } from '../components/ui.tsx';
import { Icon } from '../components/Icons.tsx';
import { initials } from '../components/Topbar.tsx';
import { api } from '../api/client.ts';
import { togglePlay } from '../components/Transport.tsx';

type Idea = { id: string; name: string; who: string; source: string; notes: { t: number; d: number; m: number; v?: number }[]; info: string; placed?: boolean };
let ideasStore: Idea[] = [];
const NOTE = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
const nn = (m: number) => `${NOTE[m % 12]}${Math.floor(m / 12) - 1}`;

export function Compose() {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '330px 1fr 400px', gap: 10, height: '100%', minHeight: 0 }}>
      <Brief />
      <div style={{ display: 'grid', gridTemplateRows: 'auto 1fr auto auto', gap: 10, minHeight: 0 }}>
        <Harmony />
        <Topline />
        <Groove />
        <Actions />
      </div>
      <div className="stack" style={{ minHeight: 0, overflow: 'auto' }}>
        <IdeaPool />
        <Lyrics />
        <Authorship />
      </div>
    </div>
  );
}

// ---------- Brief ----------
function Brief() {
  const p = useStudio((s) => s.project);
  const commit = useStudio((s) => s.commit);
  const [mode, setMode] = useState<'suggest' | 'browser'>('suggest');
  const b = p.brief ?? { client: '', text: '' };
  const len = Math.max(0, ...p.clips.map((c) => c.start + c.duration));
  const hookClip = p.clips.find((c) => /hook|lead|melod/i.test(p.tracks.find((t) => t.id === c.trackId)?.name ?? '') && c.notes?.length);
  const hookAt = hookClip ? secAt(p, beatAt(p, hookClip.start) + (hookClip.notes![0]?.t ?? 0)) : undefined;
  const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')}`;
  const checks: [string, string, boolean][] = [
    ['Tempo', `${p.bpm} BPM`, !b.bpmMin || (p.bpm >= b.bpmMin && p.bpm <= (b.bpmMax ?? 999))],
    ['Länge', fmt(len), !b.maxSeconds || len <= b.maxSeconds],
    ['Hook ab', hookAt !== undefined ? fmt(hookAt) : 'fehlt noch', hookAt !== undefined && (!b.hookBySeconds || hookAt <= b.hookBySeconds)],
    ['Tonart', p.key, true],
  ];
  return (
    <Panel title="Brief vom Label" right={<span className="pill">{p.name}</span>}>
      <div style={{ overflow: 'auto', flex: 1 }}>
        <div style={{ padding: '14px 16px 4px' }}>
          <input className="h3" style={{ border: 0, fontSize: 16, width: '100%', outline: 'none' }} value={b.client} placeholder="Auftraggeber" onChange={(e) => commit('Brief', (x) => { x.brief = { ...b, client: e.target.value }; }, { merge: true })} />
          <textarea className="t2" rows={3} style={{ border: 0, width: '100%', resize: 'none', marginTop: 6, outline: 'none', lineHeight: 1.5 }} value={b.text} placeholder="Was wünscht sich das Label?" onChange={(e) => commit('Brief', (x) => { x.brief = { ...b, text: e.target.value }; }, { merge: true })} />
        </div>
        <div className="sect">Abgleich mit dem Brief</div>
        <div style={{ padding: '0 16px' }}>
          {checks.map(([k, v, ok]) => <div className="metric" key={k}><i className="ldot" style={{ color: ok ? 'var(--green)' : 'var(--claim)' }} />{k}<b>{v}</b></div>)}
        </div>
        <div className="sect">SRS Engine</div>
        <div className="stack" style={{ padding: '0 16px 16px', gap: 8 }}>
          {([['browser', 'Im Browser', 'Harmonie, Groove, Melodie-Varianten, regelbasiert, auch offline', false], ['cloud', 'SRS Cloud', 'größere Modelle, nur auf freigegebenen Katalogwerken trainiert (Phase 5)', true], ['suggest', 'Nur Vorschläge', 'KI schreibt nichts direkt in den Song, alles landet als markierter Vorschlag', false]] as const).map(([k, t, d, dis]) => (
            <label key={k} className={`card radio ${mode === k ? 'on' : ''}`} style={{ opacity: dis ? 0.55 : 1 }}>
              <input type="radio" disabled={dis} checked={mode === k} onChange={() => setMode(k as 'suggest' | 'browser')} />
              <div><b>{t}</b><div className="t2" style={{ fontSize: 12 }}>{d}</div></div>
            </label>
          ))}
        </div>
      </div>
    </Panel>
  );
}

// ---------- Harmonie ----------
function Harmony() {
  const p = useStudio((s) => s.project);
  const commit = useStudio((s) => s.commit);
  const [sel, setSel] = useState<number | null>(null);
  const key = safeKey(p.key);
  const sections = p.markers.slice().sort((a, b) => a.time - b.time);
  const sectionOf = (t: number) => [...sections].reverse().find((m) => m.time <= t + 1e-6)?.name ?? 'Song';
  const groups: { name: string; items: { ch: Project['chords'][number]; i: number }[] }[] = [];
  p.chords.forEach((ch, i) => { const n = sectionOf(ch.time); const g = groups[groups.length - 1]; if (g?.name === n) g.items.push({ ch, i }); else groups.push({ name: n, items: [{ ch, i }] }); });
  const colors = ['#1f8a5a', '#c7861b', '#003399', '#6b4fbb'];

  const reharm = () => {
    if (sel === null) return toast('Erst einen Akkord anklicken');
    const c = parseChord(p.chords[sel].symbol);
    const minor = c.intervals.includes(3) && !c.intervals.includes(4);
    const alt = (c.root + (minor ? 3 : 9)) % 12; // Terzverwandter: Moll → Dur-Parallele, Dur → Moll-Parallele
    const sym = NOTE[alt] + (minor ? 'maj7' : 'm7');
    commit('Reharmonisieren', (x) => { x.chords[sel].symbol = sym; });
    toast(`${p.chords[sel].symbol} → ${sym} (Terzverwandter). Rückgängig mit Cmd+Z.`);
  };

  return (
    <Panel title={<>Harmonie <span className="t2" style={{ fontFamily: 'var(--font)', fontWeight: 400, fontSize: 12.5, marginLeft: 8 }}>Klick wählt, Doppelklick ändert</span></>} right={<>
      <button className="btn small" onClick={reharm}>Reharmonisieren</button>
      <button className="btn small" onClick={() => commit('Akkord', (x) => { const last = x.chords[x.chords.length - 1]; x.chords.push({ time: last ? secOfBar(x, barAtSec(x, last.time) + 2) : 0, symbol: last?.symbol ?? 'C' }); })}>+ Akkord</button>
    </>}>
      <div className="chordgroups">
        {groups.map((g, gi) => (
          <div key={gi}>
            <div className="glabel"><i style={{ background: colors[gi % 4] }} />{g.name}</div>
            <div className="row" style={{ gap: 6 }}>
              {g.items.map(({ ch, i }) => (
                <button key={i} className={`chordchip ${sel === i ? 'on' : ''}`}
                  onClick={() => { setSel(i); engine.locate(ch.time); }}
                  onDoubleClick={() => { const s = prompt('Akkord', ch.symbol); if (s) { try { parseChord(s); commit('Akkord ändern', (x) => { x.chords[i].symbol = s; }); } catch { toast('Akkord nicht erkannt'); } } }}>
                  <b>{ch.symbol}</b><small>{roman(ch.symbol, key)}</small>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
}

// ---------- Topline (Piano-Roll) ----------
function melodicClip(p: Project, selIds: string[]): Clip | undefined {
  const isMel = (c: Clip) => c.kind === 'midi' && p.tracks.find((t) => t.id === c.trackId)?.instrument?.plugin !== 'srs:drum';
  return p.clips.find((c) => selIds.includes(c.id) && isMel(c))
    ?? p.clips.find((c) => isMel(c) && /hook|lead|melod|topline/i.test(p.tracks.find((t) => t.id === c.trackId)?.name ?? ''))
    ?? p.clips.find(isMel);
}

function Topline() {
  const p = useStudio((s) => s.project);
  const sel = useStudio((s) => s.selection.clipIds);
  const me = useStudio((s) => s.user);
  const peers = useStudio((s) => s.peers);
  const commit = useStudio((s) => s.commit);
  const clip = melodicClip(p, sel);
  const notes = clip?.notes ?? [];
  const relBeat = (t: number) => (clip ? beatAt(p, t) - beatAt(p, clip.start) : 0);
  const lo = Math.min(60, ...notes.map((n) => n.m)) - 2, hi = Math.max(lo + 18, ...notes.map((n) => n.m)) + 2;
  const beats = clip ? Math.round(relBeat(clip.start + clip.duration)) : 16;
  const first = notes.length ? Math.floor(Math.min(...notes.map((n) => n.t)) / 4) * 4 : 0;
  const view = Math.max(4, Math.min(beats - first, 16));
  const colorOf = (n: Note) => n.by.userId === me.id ? me.color : peers.find((x) => x.user.id === n.by.userId)?.user.color ?? '#1f8a5a';
  const authors = [...new Set(notes.filter((n) => !n.by.ai).map((n) => n.by.userId))];
  const nameOf = (id: string) => id === me.id ? me.name : peers.find((x) => x.user.id === id)?.user.name ?? 'Beispiel';

  const click = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!clip) return;
    const r = e.currentTarget.getBoundingClientRect();
    const t = first + Math.floor(((e.clientX - r.left) / r.width) * view * 2) / 2;
    const m = hi - Math.floor(((e.clientY - r.top) / r.height) * (hi - lo + 1));
    const hit = notes.findIndex((n) => n.m === m && t >= n.t && t < n.t + n.d);
    commit(hit >= 0 ? 'Note löschen' : 'Note setzen', (x) => {
      const c = x.clips.find((y) => y.id === clip.id)!;
      if (hit >= 0) c.notes!.splice(hit, 1);
      else c.notes!.push({ t, d: 0.5, m: snapToKey(m, x.key), v: 0.8, by: { userId: me.id, at: new Date().toISOString() } });
    });
    if (hit < 0) void engine.ensure().then((ctx) => voiceFor('srs:synth', 'lead')(ctx, ctx.destination, snapToKey(m, p.key), 0.7, ctx.currentTime + 0.01, 0.25));
  };

  const variants = () => {
    if (!clip || !notes.length) return toast('Keine Melodie gewählt');
    const at = new Date().toISOString();
    commit('Engine: Varianten', (x) => {
      const t = x.tracks.find((y) => y.id === clip.trackId)!;
      const vt = emptyTrack(`${t.name} Varianten`, 'midi', '#6b4fbb', { instrument: t.instrument, mute: true });
      x.tracks.push(vt);
      const mk = (name: string, f: (n: Note, i: number) => Note | null) => x.clips.push({ ...structuredClone(clip), id: uid('c'), trackId: vt.id, name, by: { userId: 'srs-engine', ai: true, at }, notes: notes.map(f).filter((n): n is Note => !!n).map((n) => ({ ...n, by: { userId: 'srs-engine', ai: true, at } })) });
      mk('Variante A: Oktave hoch', (n) => ({ ...n, m: n.m + 12 }));
      mk('Variante B: synkopiert', (n, i) => ({ ...n, t: i % 2 ? n.t + 0.5 : n.t, d: Math.max(0.25, n.d - 0.25) }));
      mk('Variante C: ruhiger', (n, i) => (i % 3 === 2 ? null : { ...n, d: n.d * 1.5 }));
    });
    toast('3 Varianten als stumme KI-Spur angelegt. In der Session anhören und auswählen.');
  };

  return (
    <Panel title={<>Topline {clip && <span className="pill blue" style={{ marginLeft: 8 }}>{clip.name}</span>}</>} right={<>
      <div className="row" style={{ gap: 0, paddingLeft: 6 }}>{authors.map((a) => <span key={a} className="av" title={nameOf(a)} style={{ width: 22, height: 22, fontSize: 8.5, border: '2px solid #fff', background: a === me.id ? me.color : peers.find((x) => x.user.id === a)?.user.color ?? '#1f8a5a' }}>{initials(nameOf(a))}</span>)}</div>
      <span className="t2" style={{ fontSize: 12 }}>Autorenfarben</span>
      <span className="pill violet" style={{ borderStyle: 'dashed' }}>KI-Vorschlag</span>
      <button className="btn small" onClick={() => useStudio.getState().setView('session')}>In Session zeigen</button>
      <button className="btn small violet" onClick={variants}>3 Varianten</button>
    </>} style={{ minHeight: 0 }}>
      {!clip ? <div className="empty">Noch keine Melodie. Rechts eine Idee hochladen oder summen.</div> : (
        <div className="rollgrid">
          <div className="keys">{Array.from({ length: hi - lo + 1 }, (_, k) => hi - k).map((m) => <div key={m} className={[1, 3, 6, 8, 10].includes(m % 12) ? 'blk' : ''}>{m % 12 === 0 || m % 12 === 6 ? nn(m) : ''}</div>)}</div>
          <div className="notes" onClick={click} title="Klick setzt oder löscht eine Note (rastet in die Tonart ein)">
            {Array.from({ length: hi - lo + 1 }, (_, k) => hi - k).map((m, k) => <div key={m} className={`lane ${[1, 3, 6, 8, 10].includes(m % 12) ? 'blk' : ''}`} style={{ top: `${(k / (hi - lo + 1)) * 100}%`, height: `${100 / (hi - lo + 1)}%` }} />)}
            {Array.from({ length: view + 1 }, (_, b) => <div key={b} className={`beat ${b % 4 === 0 ? 'bar' : ''}`} style={{ left: `${(b / view) * 100}%` }} />)}
            {p.chords.filter((c) => { const bt = relBeat(c.time); return bt >= first && bt < first + view; }).map((c, i) => <div key={i} className="chordmark" style={{ left: `${((relBeat(c.time) - first) / view) * 100}%` }}>{c.symbol}</div>)}
            {notes.filter((n) => n.t >= first && n.t < first + view).map((n, i) => (
              <div key={i} className={`note ${n.by.ai ? 'ai' : ''}`} title={`${nn(n.m)}${n.lyric ? ` „${n.lyric}“` : ''} · ${n.by.ai ? 'KI' : nameOf(n.by.userId)}`}
                style={{ left: `${((n.t - first) / view) * 100}%`, width: `${(n.d / view) * 100}%`, top: `${((hi - n.m) / (hi - lo + 1)) * 100}%`, height: `${100 / (hi - lo + 1)}%`, background: n.by.ai ? undefined : colorOf(n) }} />
            ))}
          </div>
        </div>
      )}
    </Panel>
  );
}

// ---------- Groove (Step-Sequencer) ----------
function Groove() {
  const p = useStudio((s) => s.project);
  const commit = useStudio((s) => s.commit);
  const me = useStudio((s) => s.user);
  const drums = p.tracks.filter((t) => t.instrument?.plugin === 'srs:drum');
  const stepsOf = (trackId: string) => {
    const c = p.clips.find((x) => x.trackId === trackId && x.notes);
    const on = new Set<number>();
    c?.notes?.forEach((n) => { if (n.t < 4) on.add(Math.round(n.t * 4)); });
    return { c, on };
  };
  const toggle = (trackId: string, step: number) => {
    const { c, on } = stepsOf(trackId);
    const t = p.tracks.find((x) => x.id === trackId)!;
    const m = c?.notes?.[0]?.m ?? (/snare|clap/i.test(t.name) ? 38 : /hat/i.test(t.name) ? 42 : 36);
    commit('Groove', (x) => {
      let clip = x.clips.find((y) => y.id === c?.id);
      if (!clip) { clip = { id: uid('c'), trackId, kind: 'midi', name: t.name, start: 0, offset: 0, duration: secOfBar(x, 8), notes: [], fadeIn: 0, fadeOut: 0, by: { userId: me.id, at: new Date().toISOString() } }; x.clips.push(clip); }
      const bars = Math.round((beatAt(x, clip.start + clip.duration) - beatAt(x, clip.start)) / 4);
      if (on.has(step)) clip.notes = clip.notes!.filter((n) => Math.round((n.t % 4) * 4) !== step);
      else for (let b = 0; b < bars; b++) clip.notes!.push({ t: b * 4 + step / 4, d: 0.25, m, v: step % 4 === 0 ? 0.95 : 0.75, by: { userId: me.id, at: new Date().toISOString() } });
    });
  };
  const addHat = () => commit('Hi-Hat', (x) => { x.tracks.push(emptyTrack('Hi-Hat', 'midi', '#2f5fd0', { instrument: { plugin: 'srs:drum' }, volumeDb: -8 })); });
  return (
    <Panel title={<>Groove <span className="t2" style={{ fontFamily: 'var(--font)', fontWeight: 400, fontSize: 12.5, marginLeft: 8 }}>Klick setzt oder löscht einen Schlag, gilt für alle Takte</span></>} right={<>
      <span className="pill">{p.bpm} BPM</span>
      {!drums.some((d) => /hat/i.test(d.name)) && <button className="btn small" onClick={addHat}>+ Hi-Hat</button>}
    </>}>
      <div className="groove">
        {drums.map((t) => {
          const { on } = stepsOf(t.id);
          return (
            <div key={t.id} className="grow">
              <span>{t.name}</span>
              {Array.from({ length: 16 }, (_, s) => <button key={s} className={`gcell ${on.has(s) ? 'on' : ''} ${s % 4 === 0 ? 'q' : ''}`} style={on.has(s) ? { background: t.color } : undefined} onClick={() => toggle(t.id, s)} />)}
            </div>
          );
        })}
      </div>
    </Panel>
  );
}

function Actions() {
  const p = useStudio((s) => s.project);
  const playing = useStudio((s) => s.transport.playing);
  const refrain = p.markers.find((m) => /refrain|chorus|hook/i.test(m.name));
  const commit = useStudio((s) => s.commit);
  const pads = () => {
    if (!p.chords.length) return toast('Keine Akkorde im Projekt');
    const at = new Date().toISOString();
    commit('Engine: Akkord-Pads', (x) => {
      const t = emptyTrack('Pads (Vorschlag)', 'midi', '#6b4fbb', { instrument: { plugin: 'srs:synth', preset: 'pad' }, volumeDb: -8 });
      x.tracks.push(t);
      const notes: Note[] = [];
      x.chords.forEach((c, i) => {
        const end = x.chords[i + 1]?.time ?? secOfBar(x, barAtSec(x, c.time) + 2);
        for (const m of voicing(c.symbol, 55)) notes.push({ t: beatAt(x, c.time), d: beatAt(x, end) - beatAt(x, c.time) - 0.1, m, v: 0.5, by: { userId: 'srs-engine', ai: true, at } });
      });
      x.clips.push({ id: uid('c'), trackId: t.id, kind: 'midi', name: 'Pads aus Akkorden', start: 0, offset: 0, duration: secAt(x, Math.max(...notes.map((n) => n.t + n.d))), notes, fadeIn: 0, fadeOut: 0, by: { userId: 'srs-engine', ai: true, at } });
    });
    toast('Pads als KI-Vorschlag eingefügt. Zählt nicht für die Splits.');
  };
  return (
    <section className="panel"><div className="row" style={{ padding: 10, gap: 8 }}>
      <button className="btn primary" style={{ height: 38, padding: '0 22px' }} onClick={() => { if (playing) engine.stop(); else { engine.locate(refrain?.time ?? 0); void togglePlay(); } }}>{playing ? 'Stopp' : refrain ? `${refrain.name} anhören` : 'Anhören'}</button>
      <button className="btn" onClick={pads}><Icon name="spark" size={13} /> Pads aus Akkorden</button>
      <span className="t2" style={{ marginLeft: 'auto', fontSize: 12 }}>alle im Raum sehen Änderungen live</span>
      <button className="btn" onClick={() => useStudio.getState().setView('session')}>Zur Session</button>
    </div></section>
  );
}

// ---------- Ideen-Pool ----------
function IdeaPool() {
  const p = useStudio((s) => s.project);
  const me = useStudio((s) => s.user);
  const commit = useStudio((s) => s.commit);
  const [ideas, setIdeas] = useState<Idea[]>(ideasStore);
  const [busy, setBusy] = useState('');
  const [rec, setRec] = useState<MediaRecorder | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const add = (i: Idea) => { ideasStore = [i, ...ideasStore]; setIdeas(ideasStore); };

  const fromAudio = async (data: ArrayBuffer, name: string) => {
    setBusy('Tonhöhe erkennen …');
    try {
      const ctx = await engine.ensure();
      const buf = await ctx.decodeAudioData(data);
      const raw = detectNotes(buf.getChannelData(0), buf.sampleRate, 24);
      if (!raw.length) return toast('Keine klare Melodie gefunden. Summen oder Pfeifen ohne Begleitung klappt am besten.');
      const q = quantizeNotes(raw, p.bpm, p.key, { grid: 0.5, maxBeats: 32 });
      add({ id: uid('idea'), name, who: me.id, source: 'Audio', notes: q.notes, info: `${q.notes.length} Noten, ${buf.duration.toFixed(0)} s` });
      toast(`Idee erkannt: ${q.notes.length} Noten, in ${p.key} eingerastet.`);
    } catch { toast('Dieses Audioformat kann der Browser nicht öffnen. WAV, M4A oder MP3 funktionieren.'); }
    finally { setBusy(''); }
  };
  const onFile = async (f: File) => {
    const data = await f.arrayBuffer();
    if (/\.midi?$/i.test(f.name)) {
      try { const m = parseMidi(data); add({ id: uid('idea'), name: f.name, who: me.id, source: 'MIDI', notes: m.notes, info: `${m.notes.length} Noten` }); toast(`Idee erkannt: ${m.notes.length} Noten aus MIDI übernommen.`); }
      catch (e) { toast(`MIDI-Datei nicht lesbar: ${(e as Error).message}`); }
      return;
    }
    await fromAudio(data, f.name);
  };
  const hum = async () => {
    if (rec) { rec.stop(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: true } });
      const r = new MediaRecorder(stream); const chunks: Blob[] = [];
      r.ondataavailable = (e) => chunks.push(e.data);
      r.onstop = async () => { stream.getTracks().forEach((t) => t.stop()); setRec(null); await fromAudio(await new Blob(chunks).arrayBuffer(), 'Hook gesummt'); };
      r.start(); setRec(r);
    } catch { toast('Kein Zugriff aufs Mikrofon'); }
  };
  const place = (idea: Idea) => {
    const at = new Date().toISOString();
    const pos0 = useStudio.getState().transport.position, bl = meterAt(p, pos0).barLen;
    const len = Math.ceil(Math.max(...idea.notes.map((n) => n.t + n.d)) / bl) * bl;
    const pos = useStudio.getState().transport.position;
    commit('Idee übernehmen', (x) => {
      const t = emptyTrack(idea.name.replace(/\.(mid|midi|wav|m4a|mp3)$/i, '').slice(0, 24), 'midi', '#c7861b', { instrument: { plugin: 'srs:synth', preset: 'lead' } });
      x.tracks.push(t);
      x.clips.push({ id: uid('c'), trackId: t.id, kind: 'midi', name: idea.name, start: pos, offset: 0, duration: secAt(x, beatAt(x, pos) + len) - pos, notes: idea.notes.map((n) => ({ t: n.t, d: n.d, m: n.m, v: n.v ?? 0.8, by: { userId: idea.who, at } })), fadeIn: 0, fadeOut: 0, by: { userId: idea.who, at } });
    });
    ideasStore = ideasStore.map((i) => (i.id === idea.id ? { ...i, placed: true } : i)); setIdeas(ideasStore);
    if (useStudio.getState().token) void api.addContribution(p.id, { kind: 'melody', label: idea.name, ai: false }).catch(() => undefined);
    toast('Idee liegt als MIDI-Spur in der Session. Klang im Reiter Klang wählen.');
  };
  const preview = async (idea: Idea) => {
    const ctx = await engine.ensure(), spb = 60 / p.bpm, t0 = ctx.currentTime + 0.05;
    for (const n of idea.notes) voiceFor('srs:synth', 'lead')(ctx, ctx.destination, n.m, n.v ?? 0.8, t0 + n.t * spb, n.d * spb);
  };
  const download = (idea: Idea) => {
    const blob = new Blob([writeMidi(idea.notes.map((n) => ({ ...n, v: n.v ?? 0.8 })), p.bpm) as Uint8Array<ArrayBuffer>], { type: 'audio/midi' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `${idea.name.replace(/\W+/g, '_')}.mid`; a.click();
  };

  return (
    <Panel title="Ideen-Pool" right={<>
      <button className={`btn small ${rec ? 'danger' : ''}`} onClick={() => void hum()}>{rec ? '■ Stopp' : '● Summen'}</button>
      <button className="btn small primary" onClick={() => fileRef.current?.click()}>Idee hochladen</button>
      <input ref={fileRef} type="file" hidden accept="audio/*,.mid,.midi" onChange={(e) => e.target.files?.[0] && void onFile(e.target.files[0])} />
    </>}>
      <div onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) void onFile(f); }}>
        {busy && <div className="pill blue" style={{ margin: '10px 16px' }}>{busy}</div>}
        {ideas.length === 0 && !busy && <div className="t2" style={{ padding: '14px 16px', fontSize: 12.5 }}>Sprachmemo, gepfiffene Hook oder MIDI-Datei hier ablegen. Einstimmige Melodien ohne Begleitung werden am genauesten erkannt.</div>}
        {ideas.map((i) => (
          <div key={i.id} className="idea">
            <span className="av" style={{ width: 28, height: 28, margin: 0, border: 0, background: me.color }}>{initials(me.name)}</span>
            <div style={{ minWidth: 0 }}><b>{i.name}</b><div className="t2 mono" style={{ fontSize: 11 }}>{i.source} · {i.info}</div></div>
            <div className="row" style={{ gap: 4 }}>
              <button className="btn small" title="Anhören" onClick={() => void preview(i)}>▶</button>
              <button className="btn small" title=".mid exportieren" onClick={() => download(i)}>.mid</button>
              {i.placed ? <span className="pill green">in Session</span> : <button className="btn small primary" onClick={() => place(i)}>In Session</button>}
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
}

// ---------- Text-Werkstatt ----------
const syllables = (s: string) => (s.toLowerCase().match(/[aeiouyäöü]+/g) ?? []).length;
const rhymeKey = (line: string) => { const w = line.toLowerCase().replace(/[^a-zäöüß\s]/g, '').trim().split(/\s+/).pop() ?? ''; const m = w.match(/[aeiouyäöü]+[^aeiouyäöü]*$/); return m ? m[0] : w; };

function Lyrics() {
  const p = useStudio((s) => s.project);
  const me = useStudio((s) => s.user);
  const peers = useStudio((s) => s.peers);
  const commit = useStudio((s) => s.commit);
  const [draft, setDraft] = useState('');
  const lines = useMemo(() => p.lyrics ?? [], [p.lyrics]);
  const scheme = useMemo(() => { const keys: string[] = []; return lines.map((l) => { const k = rhymeKey(l.text); let i = keys.indexOf(k); if (i < 0) { keys.push(k); i = keys.length - 1; } return String.fromCharCode(65 + (i % 26)); }); }, [lines]);
  const colorOf = (id: string) => id === me.id ? me.color : peers.find((x) => x.user.id === id)?.user.color ?? '#8a93a6';
  const nameOf = (id: string) => id === me.id ? me.name : peers.find((x) => x.user.id === id)?.user.name ?? '?';
  const addLine = () => { if (!draft.trim()) return; commit('Textzeile', (x) => { (x.lyrics ??= []).push({ id: uid('l'), text: draft.trim(), by: { userId: me.id, at: new Date().toISOString() } }); }); setDraft(''); };
  return (
    <Panel title="Text-Werkstatt" right={<><span className="pill">Silben</span><span className="pill">Reim</span></>}>
      <div style={{ padding: '6px 16px 12px' }}>
        {lines.map((l, i) => (
          <div key={l.id} className="lyric">
            <span className="av" style={{ width: 18, height: 18, fontSize: 7.5, margin: 0, border: 0, background: colorOf(l.by.userId) }} title={nameOf(l.by.userId)}>{initials(nameOf(l.by.userId))}</span>
            <input value={l.text} onChange={(e) => commit('Text', (x) => { x.lyrics![i].text = e.target.value; }, { merge: true })} />
            <small>{syllables(l.text)}</small><b>{scheme[i]}</b>
            <button className="x" title="Zeile löschen" onClick={() => commit('Zeile löschen', (x) => { x.lyrics!.splice(i, 1); })}>×</button>
          </div>
        ))}
        <div className="lyric">
          <span className="av" style={{ width: 18, height: 18, fontSize: 7.5, margin: 0, border: 0, background: me.color }}>{initials(me.name)}</span>
          <input placeholder="Neue Zeile, Enter zum Einfügen" value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addLine()} />
          <small>{draft ? syllables(draft) : ''}</small><b />
        </div>
      </div>
    </Panel>
  );
}

// ---------- Urheberschaft ----------
function Authorship() {
  const p = useStudio((s) => s.project);
  const me = useStudio((s) => s.user);
  const peers = useStudio((s) => s.peers);
  const counts = new Map<string, number>();
  let ai = 0, all = 0;
  for (const c of p.clips) for (const n of c.notes ?? []) { all++; if (n.by.ai) ai++; else counts.set(n.by.userId, (counts.get(n.by.userId) ?? 0) + 1); }
  for (const l of p.lyrics ?? []) { all++; counts.set(l.by.userId, (counts.get(l.by.userId) ?? 0) + 1); }
  const name = (id: string) => id === me.id ? me.name : id === 'u-gast' ? 'Beispiel' : peers.find((x) => x.user.id === id)?.user.name ?? 'Writer';
  return (
    <Panel title="Urheberschaft" right={<span className={`pill ${ai === 0 ? 'green' : 'amber'}`}>{ai === 0 ? 'handgemacht' : `${Math.round((ai / Math.max(1, all)) * 100)} % KI`}</span>}>
      <div style={{ padding: '10px 16px 14px' }}>
        {[...counts.entries()].map(([id, n]) => (
          <div key={id} className="metric">{name(id)}<b>{Math.round((n / Math.max(1, all - ai)) * 100)} %</b></div>
        ))}
        <div className="metric">Melodie-Ähnlichkeit zum Katalog<b className="muted">ab Phase 5</b></div>
        <div className="t2" style={{ fontSize: 12, marginTop: 8, lineHeight: 1.5 }}>Jede Note und jede Zeile ist ihrem Autor zugeordnet und fließt in den Split-Vorschlag. KI-Noten sind violett und zählen nicht als Urheberanteil.</div>
      </div>
    </Panel>
  );
}

function safeKey(k: string) { try { return parseKey(k); } catch { return { tonic: 0, mode: 'major' as const }; } }
const ROMAN = ['I', 'II', 'II', 'III', 'III', 'IV', 'V', 'V', 'VI', 'VI', 'VII', 'VII'];
function roman(symbol: string, key: { tonic: number }) {
  try { const c = parseChord(symbol), deg = (c.root - key.tonic + 12) % 12; const minor = c.intervals.includes(3) && !c.intervals.includes(4); return minor ? ROMAN[deg].toLowerCase() : ROMAN[deg]; } catch { return ''; }
}
